// Shared plumbing for the Content module's CSV bulk uploads
// (Courses, Chapters, Lessons, Practice Lists, Practice Items).
//
// Every bulk upload is "CSV in, records out": the drawer holds no dropdowns or
// autocompletes, so each row carries its own relationships and field values.
// These helpers turn raw cell strings into the exact payload shape the matching
// single Create/Edit form posts, and run rows one at a time so a bad row never
// stops the good ones.

import { LINE_KEY } from './csv'

const TRUE_WORDS = ['yes', 'y', 'true', '1', 'active', 'enabled', 'on']
const FALSE_WORDS = ['no', 'n', 'false', '0', 'inactive', 'disabled', 'off']
const ADMIN_ONLY_WORDS = ['adminonly', 'admin', 'admins', 'unpublished', 'draft', 'hidden', 'true', 'yes']

const normalizeKey = value =>
  String(value ?? '')
    .toLowerCase()
    .replace(/[\s_-]/g, '')

const text = value => String(value ?? '').trim()

const equalsIgnoreCase = (a, b) => text(a).toLowerCase() === text(b).toLowerCase()

/**
 * Header matching is forgiving: "Video URL", "video_url" and "videoUrl" all land
 * on the template's canonical `videoUrl` column. Values are trimmed so a stray
 * space in a spreadsheet cell can't break a lookup.
 */
export const canonicalizeRow = (row, headers) => {
  const byKey = {}
  Object.keys(row || {}).forEach(key => {
    byKey[normalizeKey(key)] = row[key]
  })
  const canonical = headers.reduce((acc, header) => {
    acc[header] = text(byKey[normalizeKey(header)])
    return acc
  }, {})
  // Carried through so errors can name the line the author sees, not the
  // position the row happens to hold after blank lines were dropped.
  if (row?.[LINE_KEY]) canonical[LINE_KEY] = row[LINE_KEY]
  return canonical
}

// The spreadsheet line a row came from, falling back to its position.
export const lineOf = (row, index) => row?.[LINE_KEY] ?? index + 2

/**
 * Guards against someone uploading the wrong template, and reports the columns
 * that will simply be ignored. Throws when nothing at all lines up.
 */
export const inspectHeaders = (row, headers) => {
  const present = Object.keys(row || {}).filter(key => text(key) && key !== LINE_KEY)
  const isKnown = key => headers.some(header => normalizeKey(header) === normalizeKey(key))
  if (!present.some(isKnown)) {
    throw new Error(
      'None of the columns in this file match the sample CSV. Download the sample and keep its column headers.'
    )
  }
  return present.filter(key => !isKnown(key))
}

export const parseBoolean = (value, fallback = false) => {
  const normalized = normalizeKey(value)
  if (!normalized) return fallback
  if (TRUE_WORDS.includes(normalized)) return true
  if (FALSE_WORDS.includes(normalized)) return false
  return fallback
}

// `status` column -> isActive. Blank means Active, matching the Create form.
export const parseStatus = value => parseBoolean(value, true)

// `visibility` column -> unPublished. Blank means visible to students.
export const parseVisibility = value => {
  const normalized = normalizeKey(value)
  if (!normalized) return false
  return ADMIN_ONLY_WORDS.includes(normalized)
}

export const requireValues = (row, columns) => {
  const missing = columns.filter(column => !row[column])
  if (missing.length) {
    throw new Error(`Missing required ${missing.length > 1 ? 'columns' : 'column'}: ${missing.join(', ')}`)
  }
}

export const parseNumber = (value, column, { fallback = '', min } = {}) => {
  if (!value) return fallback
  const parsed = Number(value)
  if (Number.isNaN(parsed)) throw new Error(`${column} must be a number (got "${value}")`)
  if (min !== undefined && parsed < min) {
    throw new Error(`${column} cannot be less than ${min} (got "${value}")`)
  }
  return parsed
}

/**
 * Applies the same length limits the matching single form declares in its Yup
 * schema, so a CSV row can't create a record the Create/Edit form would have
 * rejected. Blank values are left to `requireValues`.
 */
export const validateLengths = (row, rules) => {
  Object.keys(rules).forEach(column => {
    const { min, max, label = column } = rules[column]
    const value = text(row[column])
    if (!value) return
    if (min !== undefined && value.length < min) {
      throw new Error(`${label} must be at least ${min} characters (got ${value.length})`)
    }
    if (max !== undefined && value.length > max) {
      throw new Error(`${label} cannot be more than ${max} characters (got ${value.length})`)
    }
  })
}

// hrs/minutes/seconds are stored as zero-padded strings ("00".."59"), the same
// values the duration dropdowns produce.
export const parseTimeUnit = (value, column, max) => {
  if (!value) return '00'
  const parsed = Number(value)
  if (Number.isNaN(parsed) || parsed < 0 || parsed > max || !Number.isInteger(parsed)) {
    throw new Error(`${column} must be a whole number between 0 and ${max} (got "${value}")`)
  }
  return String(parsed).padStart(2, '0')
}

/** Resolve a CSV label ("Beginner") or stored key ("beginner") to the stored key. */
export const resolveOptionKey = (options, value, column, { fallback } = {}) => {
  if (!text(value)) {
    if (fallback !== undefined) return fallback
    throw new Error(`${column} is required`)
  }
  const match = (options || []).find(
    option => equalsIgnoreCase(option.key, value) || equalsIgnoreCase(option.value, value)
  )
  if (!match) {
    const allowed = (options || []).map(option => option.value).join(', ')
    throw new Error(`Unknown ${column} "${value}"${allowed ? `. Allowed values: ${allowed}` : ''}`)
  }
  return match.key
}

// Language -> Course -> Chapter -> Lesson, as the dropdown payload models it.
// Each level is scoped to the one above so "Chapter 1" only has to be unique
// inside its own course, exactly like the cascading selects on the single forms.
const CONTENT_LEVELS = [
  { column: 'language', field: 'course', label: 'Language', list: 'courses' },
  { column: 'course', field: 'unit', label: 'Course', list: 'units', parentField: 'course' },
  { column: 'chapter', field: 'chapter', label: 'Chapter', list: 'chapters', parentField: 'unit' },
  { column: 'lesson', field: 'lesson', label: 'Lesson', list: 'lessons', parentField: 'chapter' },
]

export const CONTENT_PATH_DEPTH = {
  LANGUAGE: 1,
  COURSE: 2,
  CHAPTER: 3,
  LESSON: 4,
}

/**
 * Turns the relationship columns of one row into the ids the API expects,
 * e.g. { course, unit, chapter, lesson }. Accepts either the record's name or
 * its id in each column.
 */
export const resolveContentPath = (dropdowns, row, depth) => {
  if (!dropdowns) {
    throw new Error('Reference data has not finished loading — please retry in a moment')
  }

  const path = {}
  let resolvedParentId = null

  for (let level = 0; level < depth; level++) {
    const { column, field, label, list, parentField } = CONTENT_LEVELS[level]
    const value = row[column]
    if (!value) throw new Error(`${label} is required`)

    const parentId = resolvedParentId
    const options = dropdowns[list] || []
    const scoped = parentField ? options.filter(option => option[parentField] === parentId) : options
    const matches = scoped.filter(
      option => equalsIgnoreCase(option.key, value) || equalsIgnoreCase(option.value, value)
    )

    const parent = CONTENT_LEVELS[level - 1]
    const within = parent ? ` under ${parent.label} "${row[parent.column]}"` : ''

    if (!matches.length) throw new Error(`Unknown ${label} "${value}"${within}`)
    if (matches.length > 1) {
      throw new Error(
        `${matches.length} ${label}s are named "${value}"${within} — put the ${label} id in the "${column}" column instead`
      )
    }

    path[field] = matches[0].key
    resolvedParentId = matches[0].key
  }

  return path
}

/** Split a multi-value cell such as `practiceItems` ("Item A;Item B"). */
export const splitList = value =>
  text(value)
    .split(';')
    .map(entry => entry.trim())
    .filter(Boolean)

export const describeError = (error, fallback = 'Server rejected this row') => {
  if (!error) return fallback
  if (typeof error === 'string') return error
  return (
    error.response?.data?.message ||
    error.response?.data?._message ||
    error.response?.data?.error ||
    error.message ||
    fallback
  )
}

/**
 * Runs every row, keeping the successes even when some rows fail. Returns the
 * summary the results panel renders. `line` is the spreadsheet line number
 * (row 1 is the header), so an error points at the row the user can see.
 */
export const importRows = async (rows, importRow) => {
  const failures = []
  let successCount = 0

  for (let index = 0; index < rows.length; index++) {
    const row = rows[index]
    try {
      await importRow(row, index)
      successCount++
    } catch (error) {
      failures.push({
        line: lineOf(row, index),
        name: row?.name || '(blank)',
        reason: describeError(error),
      })
    }
  }

  return { total: rows.length, successCount, failures }
}

/** Builds the sample-CSV matrix from a header list plus partial example rows. */
export const buildSampleRows = (headers, examples) => [
  headers,
  ...examples.map(example => headers.map(header => example[header] ?? '')),
]
